import { randomUUID } from 'node:crypto';
import {
  createTaskListTools,
  FileTaskListStore,
  maybeAppendTaskReminder,
  type TaskLifecycleHook,
  type TaskListStore,
} from '../../task-list';
import type { Tool } from '../../tool';
import type { AgentInputItem } from '../../types';
import type { Manifest } from '../manifest';
import type { SandboxSessionLike } from '../session';
import {
  Capability,
  type CapabilityProcessContextOptions,
  requireBoundSession,
} from './base';

const TASK_LIST_INSTRUCTIONS = `使用 TaskCreate 工具拆分并管理你的工作。这些工具有助于规划工作，并帮助用户跟踪你的进展。每完成一项任务，就立即将其标记为已完成。不要等多项任务都完成后再批量标记`;

export type TaskListArgs = {
  id?: string;
  rootDir?: string;
  store?: TaskListStore;
  reminder?: boolean;
  onTaskCreated?: TaskLifecycleHook;
  onTaskCompleted?: TaskLifecycleHook;
};

export class TaskListCapability extends Capability {
  readonly type = 'taskList';
  private readonly id?: string;
  private readonly store: TaskListStore;
  private readonly reminder: boolean;
  private readonly onTaskCreated?: TaskLifecycleHook;
  private readonly onTaskCompleted?: TaskLifecycleHook;

  constructor(args: TaskListArgs = {}) {
    super();
    this.id = args.id;
    this.store = args.store ?? new FileTaskListStore(args.rootDir);
    this.reminder = args.reminder ?? true;
    this.onTaskCreated = args.onTaskCreated;
    this.onTaskCompleted = args.onTaskCompleted;
  }

  override tools(): Tool<any>[] {
    const session = requireBoundSession(this.type, this._session);
    return createTaskListTools({
      store: this.store,
      getTaskListId: () => getOrCreateTaskListId(session, this.id),
      onTaskCreated: this.onTaskCreated,
      onTaskCompleted: this.onTaskCompleted,
    });
  }

  override instructions(_manifest: Manifest): string {
    return TASK_LIST_INSTRUCTIONS;
  }

  override async processContext(
    context: AgentInputItem[],
    options?: CapabilityProcessContextOptions,
  ): Promise<AgentInputItem[]> {
    if (!this.reminder || !this._session) {
      return context;
    }

    return await maybeAppendTaskReminder({
      input: context,
      store: this.store,
      taskListId: getOrCreateTaskListId(this._session, this.id),
      runContext: options?.runState?._context,
      currentTurn: options?.runState?._currentTurn,
    });
  }
}

export type TaskList = TaskListCapability;

export function taskList(args: TaskListArgs = {}): TaskList {
  return new TaskListCapability(args);
}

function getOrCreateTaskListId(
  session: SandboxSessionLike,
  explicitId?: string,
): string {
  if (explicitId) {
    return explicitId;
  }

  const existing = session.state.taskListId;
  if (typeof existing === 'string' && existing.trim()) {
    return existing;
  }

  const workspaceRootPath = session.state.workspaceRootPath;
  const generated =
    typeof workspaceRootPath === 'string' && workspaceRootPath.trim()
      ? `sandbox:${workspaceRootPath}`
      : `sandbox:${randomUUID()}`;
  session.state.taskListId = generated;
  return generated;
}
